<div class="sync-modal-backdrop" [class.is-closing]="isClosing" (click)="onBackdropClick($event)">
  <div class="sync-modal" role="dialog" aria-modal="true" aria-label="Calculate Sync">

    <!-- ── Header ── -->
    <div class="sync-modal__header">
      <div class="sync-modal__title">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
          <circle cx="7.5" cy="7.5" r="6" stroke="#34d399" stroke-width="1.5"/>
          <circle cx="7.5" cy="7.5" r="2.5" fill="#34d399"/>
        </svg>
        Calculate Sync
      </div>
      <button class="sync-modal__close" (click)="close()" aria-label="Close">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <line x1="1" y1="1" x2="11" y2="11" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round"/>
          <line x1="11" y1="1" x2="1"  y2="11" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <!-- ── Body ── -->
    <div class="sync-modal__body">

      <!-- Tip -->
      <p class="sync-modal__tip">
        Stay quiet, then press <strong>Start Test</strong>. If using
        <strong>headphones</strong>, hold your mic near one ear cup. If using
        <strong>speakers</strong>, move your mic close to them. The tool plays a
        click and measures how long it takes your mic to hear it.
      </p>

      <!-- Mic in use -->
      <div class="sync-modal__mic-row">
        <svg width="11" height="11" viewBox="0 0 20 20" fill="none">
          <rect x="7" y="1" width="6" height="11" rx="3" stroke="currentColor" stroke-width="1.8"/>
          <path d="M4 10c0 3.314 2.686 6 6 6s6-2.686 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <line x1="10" y1="16" x2="10" y2="19" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        <span class="sync-modal__mic-label">
          <span *ngIf="micLabel">{{ micLabel }}</span>
          <span *ngIf="!micLabel" class="sync-modal__mic-fallback">System default mic</span>
        </span>
        <button
          class="sync-modal__boost-btn"
          [class.is-on]="micBoostOn"
          [disabled]="phase === 'measuring'"
          (click)="toggleMicBoost()"
          type="button"
          ssTooltip="Boost the microphone level for the sync test only">
          Boost Mic <span class="sync-modal__boost-pill">{{ micBoostOn ? 'ON' : 'OFF' }}</span>
        </button>
      </div>

      <!-- Current recorder sync -->
      <div class="sync-modal__row">
        <span class="sync-modal__label">Current Recorder Offset</span>
        <span class="sync-modal__value" [class.is-set]="currentSync !== 0">
          {{ currentSync > 0 ? '+' : '' }}{{ currentSync }} ms
        </span>
      </div>

      <!-- Click volume selector -->
      <div class="sync-modal__row sync-modal__row--levels" ssTooltip="Adjust how loud the sync test click plays">
        <span class="sync-modal__label">Click volume</span>
        <div class="sync-modal__levels">
          <button
            *ngFor="let l of [0, 1, 2]"
            class="sync-modal__level-btn"
            [class.is-active]="clickLevel === l"
            [disabled]="phase === 'measuring'"
            (click)="setClickLevel(l)"
            type="button">
            {{ levelLabels[l] }}
          </button>
        </div>
      </div>

      <!-- Waveform canvas -->
      <div class="sync-modal__viz-wrap" ssTooltip="Shows whether your microphone is detecting sound">
        <canvas #vizCanvas class="sync-modal__viz"></canvas>
        <span class="sync-modal__viz-label">MIC INPUT</span>
        <div class="sync-modal__viz-idle" *ngIf="phase === 'ready' || phase === 'done' || phase === 'error'">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="8" y="2" width="4" height="10" rx="2" stroke="#4b5563" stroke-width="1.5"/>
            <path d="M4 10c0 3.314 2.686 6 6 6s6-2.686 6-6" stroke="#4b5563" stroke-width="1.5" stroke-linecap="round"/>
            <line x1="10" y1="16" x2="10" y2="19" stroke="#4b5563" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
      </div>

      <!-- Run indicators -->
      <div class="sync-modal__runs">
        <div
          *ngFor="let run of runs; let i = index"
          class="sync-modal__run-dot"
          [class.is-idle]="run.state === 'idle'"
          [class.is-active]="run.state === 'active'"
          [class.is-done]="run.state === 'done'"
          [class.is-error]="run.state === 'error'">
          <span *ngIf="run.state === 'done'">{{ run.ms }}ms</span>
          <span *ngIf="run.state === 'active'">
            <i class="fa fa-spinner fa-pulse"></i>
          </span>
          <span *ngIf="run.state === 'idle' || run.state === 'error'">{{ i + 1 }}</span>
        </div>
      </div>

      <!-- Previous result (dim hint, only when no fresh result yet) -->
      <div class="sync-modal__prev-result" *ngIf="previousOffset !== null && measuredOffset === null">
        <span class="sync-modal__prev-label">Last measurement</span>
        <span class="sync-modal__prev-value">{{ previousOffset > 0 ? '+' : '' }}{{ previousOffset }} ms</span>
      </div>

      <!-- Result -->
      <div class="sync-modal__result" *ngIf="measuredOffset !== null" [class]="'sync-modal__result--' + resultClass" ssTooltip="The sync value SingSnap recommends based on the test">
        <div class="sync-modal__result-value">
          {{ measuredOffset > 0 ? '+' : '' }}{{ measuredOffset }} ms
        </div>
        <div class="sync-modal__result-desc">{{ resultDescription }}</div>
      </div>

      <!-- Error -->
      <div class="sync-modal__error" *ngIf="errorMsg">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="#f87171" stroke-width="1.5"/>
          <line x1="7" y1="4" x2="7" y2="8" stroke="#f87171" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="7" cy="10" r="0.75" fill="#f87171"/>
        </svg>
        {{ errorMsg }}
      </div>

    </div>

    <!-- ── Footer ── -->
    <div class="sync-modal__footer">

      <!-- Hint bar -->
      <div class="sync-modal__hint" [class.is-visible]="tooltipSvc.text$ | async" aria-live="polite" aria-atomic="true">
        {{ tooltipSvc.text$ | async }}
      </div>

      <!-- Left: action buttons -->
      <div class="sync-modal__footer-left">
        <button
          *ngIf="phase === 'ready' || phase === 'error'"
          class="sync-modal__btn sync-modal__btn--primary"
          (click)="startTest()"
          type="button"
          ssTooltip="Begin the sync measurement test">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" stroke-width="1.5"/>
            <circle cx="6.5" cy="6.5" r="2"   fill="currentColor"/>
          </svg>
          Start Test
        </button>

        <button
          *ngIf="phase === 'measuring'"
          class="sync-modal__btn sync-modal__btn--measuring"
          disabled
          type="button">
          <i class="fa fa-spinner fa-pulse"></i>
          Measuring…
        </button>

        <button
          *ngIf="phase === 'done'"
          class="sync-modal__btn sync-modal__btn--ghost"
          (click)="reset()"
          type="button"
          ssTooltip="Run the sync test again">
          Run Again
        </button>
      </div>

      <!-- Right: apply / reset -->
      <div class="sync-modal__footer-right">
        <span class="sync-modal__applied" *ngIf="lastApplied !== null">
          ✓ Applied {{ lastApplied > 0 ? '+' : '' }}{{ lastApplied }} ms
        </span>
        <button
          class="sync-modal__btn sync-modal__btn--ghost sync-modal__btn--danger"
          (click)="resetRecorderSync()"
          type="button"
          ssTooltip="Reset sync back to the default timing">
          Reset to 0
        </button>
        <button
          class="sync-modal__btn sync-modal__btn--apply"
          [disabled]="measuredOffset === null"
          (click)="applyToRecorder()"
          type="button"
          ssTooltip="Apply the recommended sync value to this recording">
          Apply to Recorder
        </button>
      </div>

    </div>

  </div>
</div>
